/* ============================================================
   APEX LABS theme.js
   Loading screen · nav · AJAX cart drawer · predictive search
   popups · cookie banner · wishlist · recently viewed · misc UI
   ============================================================ */
(function () {
  'use strict';

  const $  = (s, c) => (c || document).querySelector(s);
  const $$ = (s, c) => Array.from((c || document).querySelectorAll(s));

  /* ---------- Money formatting ---------- */
  function formatMoney(cents) {
    const format = (window.theme && window.theme.moneyFormat) || '€{{amount}}';
    const amount = (cents / 100).toFixed(2);
    return format.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }

  /* ============================================================
     1. LOADING SCREEN
     Shown instantly via inline CSS; hidden after window "load"
     with a minimum display time so it never flashes.
     Fallback timeout guarantees it always disappears.
     ============================================================ */
  const loader = $('#bx-loader');
  const LOADER_MIN = 700;    // minimum visible time (ms)
  const LOADER_MAX = 6000;   // hard fallback
  const loaderStart = performance.now();

  function hideLoader() {
    if (!loader || loader.classList.contains('is-done')) return;
    const elapsed = performance.now() - loaderStart;
    const wait = Math.max(0, LOADER_MIN - elapsed);
    setTimeout(() => {
      loader.classList.add('is-done');
      document.documentElement.classList.remove('ax-loading');
      setTimeout(() => loader.remove(), 700);
    }, wait);
  }
  window.addEventListener('load', hideLoader);
  setTimeout(hideLoader, LOADER_MAX);
  window.addEventListener('pageshow', (e) => { if (e.persisted) hideLoader(); });

  /* ============================================================
     2. MOBILE NAV
     ============================================================ */
  const mobileNav = $('#MobileNav');
  $$('[data-nav-open]').forEach(b => b.addEventListener('click', () => {
    mobileNav && mobileNav.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }));
  $$('[data-nav-close]').forEach(b => b.addEventListener('click', closeNav));
  function closeNav() {
    mobileNav && mobileNav.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  /* ============================================================
     3. CART DRAWER (AJAX)
     ============================================================ */
  const drawer = $('#CartDrawer');

  function openDrawer() {
    if (!drawer) return;
    drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }
  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';
  }
  $$('[data-cart-open]').forEach(b => b.addEventListener('click', (e) => { e.preventDefault(); openDrawer(); }));
  $$('[data-cart-close]').forEach(b => b.addEventListener('click', closeDrawer));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { closeDrawer(); closeNav(); closeSearch(); closeModals(); }
  });

  async function fetchCart() {
    const res = await fetch(window.theme.routes.cart_url + '.js');
    return res.json();
  }

  function renderCart(cart) {
    // Badge
    $$('[data-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
    // Lines
    const list = $('[data-cart-items]');
    if (list) {
      if (cart.items.length === 0) {
        list.innerHTML = '<p class="cart-note" style="padding:32px 0">' + (list.dataset.emptyText || 'Your cart is empty.') + '</p>';
      } else {
        list.innerHTML = cart.items.map(item => `
          <div class="cart-line" data-line-key="${item.key}">
            <img src="${item.image ? item.image.replace(/(\.[a-z]+)(\?|$)/, '_160x$1$2') : ''}" alt="" width="78" height="78" loading="lazy">
            <div>
              <p class="cart-line__title">${item.product_title}</p>
              ${item.variant_title ? `<p class="cart-line__meta">${item.variant_title}</p>` : ''}
              ${item.selling_plan_allocation ? `<p class="cart-line__meta">${item.selling_plan_allocation.selling_plan.name}</p>` : ''}
              <div class="qty">
                <button type="button" data-qty-change="-1" aria-label="Decrease quantity">−</button>
                <input type="number" value="${item.quantity}" min="0" data-qty-input inputmode="numeric">
                <button type="button" data-qty-change="1" aria-label="Increase quantity">+</button>
              </div>
            </div>
            <div style="text-align:right;display:grid;gap:8px">
              <strong>${formatMoney(item.final_line_price)}</strong>
              <button type="button" class="cart-line__remove" data-line-remove>${list.dataset.removeText || 'Remove'}</button>
            </div>
          </div>`).join('');
      }
    }
    // Subtotal
    $$('[data-cart-subtotal]').forEach(el => el.textContent = formatMoney(cart.items_subtotal_price));
    // Free shipping bar
    const bar = $('[data-ship-bar]');
    if (bar) {
      const threshold = window.theme.settings.freeShippingThreshold;
      const remaining = Math.max(0, threshold - cart.items_subtotal_price);
      const pct = Math.min(100, (cart.items_subtotal_price / threshold) * 100);
      bar.classList.toggle('is-unlocked', remaining === 0);
      const msg = $('[data-ship-msg]', bar);
      if (msg) msg.textContent = remaining === 0
        ? window.theme.strings.freeShippingUnlocked
        : window.theme.strings.freeShippingRemaining.replace('{{ amount }}', formatMoney(remaining));
      const fill = $('.ship-bar__fill', bar);
      if (fill) fill.style.width = pct + '%';
    }
  }

  async function updateLine(key, quantity) {
    const res = await fetch(window.theme.routes.cart_change_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    renderCart(await res.json());
  }

  // Delegated events inside drawer
  if (drawer) {
    drawer.addEventListener('click', (e) => {
      const line = e.target.closest('.cart-line');
      if (!line) return;
      const key = line.dataset.lineKey;
      const input = $('[data-qty-input]', line);
      if (e.target.matches('[data-qty-change]')) {
        const next = Math.max(0, parseInt(input.value || '0', 10) + parseInt(e.target.dataset.qtyChange, 10));
        updateLine(key, next);
      }
      if (e.target.matches('[data-line-remove]')) updateLine(key, 0);
    });
    drawer.addEventListener('change', (e) => {
      if (e.target.matches('[data-qty-input]')) {
        const line = e.target.closest('.cart-line');
        updateLine(line.dataset.lineKey, Math.max(0, parseInt(e.target.value || '0', 10)));
      }
    });
  }

  /* Add to cart — any form with [data-ajax-atc] and quick-add buttons */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-ajax-atc]');
    if (!form) return;
    e.preventDefault();
    const btn = $('[type="submit"]', form);
    const original = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = '…'; }
    try {
      const res = await fetch(window.theme.routes.cart_add_url + '.js', {
        method: 'POST', body: new FormData(form)
      });
      if (!res.ok) throw new Error('add failed');
      renderCart(await fetchCart());
      if (btn) btn.textContent = window.theme.strings.added;
      openDrawer();
    } catch (err) {
      if (btn) btn.textContent = window.theme.strings.soldOut;
    } finally {
      setTimeout(() => { if (btn) { btn.disabled = false; btn.textContent = original; } }, 1400);
    }
  });

  document.addEventListener('click', async (e) => {
    const quick = e.target.closest('[data-quick-add]');
    if (!quick) return;
    e.preventDefault();
    quick.disabled = true;
    try {
      const res = await fetch(window.theme.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(quick.dataset.quickAdd, 10), quantity: 1 })
      });
      if (!res.ok) throw new Error();
      renderCart(await fetchCart());
      openDrawer();
    } catch (err) { /* keep silent, button re-enables */ }
    quick.disabled = false;
  });

  // Initial cart state
  if (window.theme) fetchCart().then(renderCart).catch(() => {});

  /* ============================================================
     4. PREDICTIVE SEARCH
     ============================================================ */
  const searchOverlay = $('#SearchOverlay');
  const searchInput = searchOverlay && $('input[type="search"]', searchOverlay);
  const searchResults = searchOverlay && $('[data-search-results]', searchOverlay);
  let searchTimer;

  $$('[data-search-open]').forEach(b => b.addEventListener('click', () => {
    if (!searchOverlay) return;
    searchOverlay.classList.add('is-open');
    searchInput && searchInput.focus();
  }));
  $$('[data-search-close]').forEach(b => b.addEventListener('click', closeSearch));
  function closeSearch() { searchOverlay && searchOverlay.classList.remove('is-open'); }

  if (searchInput) {
    searchInput.addEventListener('input', () => {
      clearTimeout(searchTimer);
      const q = searchInput.value.trim();
      if (q.length < 2) { searchResults.innerHTML = ''; return; }
      searchTimer = setTimeout(async () => {
        try {
          const url = `${window.theme.routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
          const res = await fetch(url);
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const inner = doc.querySelector('#PredictiveResults');
          searchResults.innerHTML = inner ? inner.innerHTML : '';
        } catch (err) { /* noop */ }
      }, 250);
    });
  }

  /* ============================================================
     5. POPUPS — newsletter (delay) & exit intent, cookie banner
     ============================================================ */
  function closeModals() { $$('.modal.is-open').forEach(m => m.classList.remove('is-open')); }
  document.addEventListener('click', (e) => {
    if (e.target.matches('[data-modal-close]') || e.target.classList.contains('modal')) closeModals();
  });

  const cookieBanner = $('#CookieBanner');
  if (cookieBanner && !localStorage.getItem('ax_cookie_choice')) {
    setTimeout(() => cookieBanner.classList.add('is-visible'), 1200);
    cookieBanner.addEventListener('click', (e) => {
      const choice = e.target.dataset.cookieChoice;
      if (!choice) return;
      localStorage.setItem('ax_cookie_choice', choice);
      cookieBanner.classList.remove('is-visible');
      if (choice === 'accept' && window.Shopify && Shopify.customerPrivacy) {
        Shopify.customerPrivacy.setTrackingConsent(true, () => {});
      }
    });
  }

  /* ============================================================
     6. WISHLIST (localStorage) + RECENTLY VIEWED
     ============================================================ */
  const WISH_KEY = 'ax_wishlist';
  const getWish = () => JSON.parse(localStorage.getItem(WISH_KEY) || '[]');
  const setWish = (a) => localStorage.setItem(WISH_KEY, JSON.stringify(a));

  function paintWishButtons() {
    const list = getWish();
    $$('[data-wishlist-toggle]').forEach(b =>
      b.classList.toggle('is-active', list.includes(b.dataset.wishlistToggle)));
  }
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-wishlist-toggle]');
    if (!b) return;
    e.preventDefault();
    const handle = b.dataset.wishlistToggle;
    let list = getWish();
    list = list.includes(handle) ? list.filter(h => h !== handle) : list.concat(handle);
    setWish(list);
    paintWishButtons();
  });
  paintWishButtons();

  // Recently viewed: record on product pages, render where a container exists
  const RV_KEY = 'ax_recent';
  const productMeta = $('[data-product-handle]');
  if (productMeta) {
    let recent = JSON.parse(localStorage.getItem(RV_KEY) || '[]')
      .filter(h => h !== productMeta.dataset.productHandle);
    recent.unshift(productMeta.dataset.productHandle);
    localStorage.setItem(RV_KEY, JSON.stringify(recent.slice(0, 8)));
  }
  const rvContainer = $('[data-recently-viewed]');
  if (rvContainer) {
    const handles = JSON.parse(localStorage.getItem(RV_KEY) || '[]')
      .filter(h => !productMeta || h !== productMeta.dataset.productHandle)
      .slice(0, 4);
    if (handles.length) {
      Promise.all(handles.map(h =>
        fetch(`/products/${h}.js`).then(r => r.ok ? r.json() : null).catch(() => null)
      )).then(products => {
        const cards = products.filter(Boolean).map(p => `
          <div class="product-card">
            <div class="product-card__media"><img src="${p.featured_image || ''}" alt="${p.title}" loading="lazy"></div>
            <div class="product-card__body">
              <p class="product-card__type">${p.type || ''}</p>
              <h3 class="product-card__title"><a href="${p.url}">${p.title}</a></h3>
              <p class="product-card__price">${formatMoney(p.price)}</p>
            </div>
          </div>`).join('');
        if (cards) {
          rvContainer.innerHTML = `<h2 style="margin-bottom:24px">${rvContainer.dataset.heading || 'Recently viewed'}</h2><div class="product-grid">${cards}</div>`;
        }
      });
    }
  }

  /* ============================================================
     7. PRODUCT PAGE — gallery, variants, selling plans, qty
     ============================================================ */
  const gallery = $('[data-gallery]');
  if (gallery) {
    gallery.addEventListener('click', (e) => {
      const t = e.target.closest('[data-thumb]');
      if (!t) return;
      $$('[data-thumb]', gallery).forEach(x => x.classList.remove('is-active'));
      t.classList.add('is-active');
      const main = $('[data-main-media]', gallery);
      if (main) main.innerHTML = t.dataset.mediaType === 'video'
        ? `<video src="${t.dataset.src}" controls autoplay muted playsinline></video>`
        : `<img src="${t.dataset.src}" alt="${t.dataset.alt || ''}">`;
    });
  }

  const productForm = $('form[data-product-form]');
  if (productForm) {
    const variantsData = $('#ProductVariants');
    const variants = variantsData ? JSON.parse(variantsData.textContent) : [];

    function currentVariant() {
      const selected = $$('.variant-pill input:checked', productForm).map(i => i.value);
      if (!selected.length) return variants[0];
      return variants.find(v => selected.every((val, i) => v.options[i] === val)) || variants[0];
    }
    function syncVariant() {
      const v = currentVariant();
      if (!v) return;
      const idInput = $('input[name="id"]', productForm);
      if (idInput) idInput.value = v.id;
      $$('[data-price]').forEach(el => el.textContent = formatMoney(v.price));
      $$('[data-compare-price]').forEach(el => {
        el.hidden = !(v.compare_at_price > v.price);
        if (!el.hidden) el.textContent = formatMoney(v.compare_at_price);
      });
      const atc = $('[data-atc]', productForm);
      if (atc) {
        atc.disabled = !v.available;
        atc.textContent = v.available ? window.theme.strings.addToCart : window.theme.strings.soldOut;
      }
    }
    productForm.addEventListener('change', (e) => {
      if (e.target.closest('.variant-pill')) syncVariant();
      // Selling plan toggle
      const opt = e.target.closest('.purchase-opt input');
      if (opt) {
        const planInput = $('input[name="selling_plan"]', productForm);
        if (planInput) planInput.value = opt.dataset.sellingPlan || '';
      }
    });
    syncVariant();
  }

  document.addEventListener('click', (e) => {
    const q = e.target.closest('[data-qty-btn]');
    if (!q || q.closest('#CartDrawer')) return;
    const input = $('input', q.parentElement);
    const next = Math.max(1, parseInt(input.value || '1', 10) + parseInt(q.dataset.qtyBtn, 10));
    input.value = next;
  });

  /* ============================================================
     8. MISC — testimonial nav, facets toggle, reveal on scroll
     ============================================================ */
  $$('[data-scroll-track]').forEach(section => {
    const track = $('.testi-track', section);
    $$('[data-scroll-dir]', section).forEach(b => b.addEventListener('click', () => {
      track.scrollBy({ left: parseInt(b.dataset.scrollDir, 10) * 320, behavior: 'smooth' });
    }));
  });

  const facetToggle = $('[data-facets-toggle]');
  if (facetToggle) facetToggle.addEventListener('click', () =>
    $('#Facets').classList.toggle('is-open'));

  const sortSelect = $('[data-sort]');
  if (sortSelect) sortSelect.addEventListener('change', () => {
    const url = new URL(location.href);
    url.searchParams.set('sort_by', sortSelect.value);
    location.href = url.toString();
  });

  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(entries => entries.forEach(en => {
      if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
    }), { threshold: 0.12 });
    $$('.reveal').forEach(el => io.observe(el));
  } else {
    $$('.reveal').forEach(el => el.classList.add('is-in'));
  }

  /* ============================================================
     9. 3D TILT — product cards follow the pointer (desktop only)
     ============================================================ */
  if (window.matchMedia('(hover:hover) and (pointer:fine)').matches &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    var MAX_TILT = 7; // degrees
    document.addEventListener('pointermove', function (e) {
      var card = e.target.closest('.product-card');
      if (!card) return;
      var r = card.getBoundingClientRect();
      var rx = ((e.clientY - r.top) / r.height - 0.5) * -MAX_TILT;
      var ry = ((e.clientX - r.left) / r.width - 0.5) * MAX_TILT;
      card.classList.add('tilting');
      card.style.transform = 'perspective(900px) rotateX(' + rx.toFixed(2) + 'deg) rotateY(' + ry.toFixed(2) + 'deg) translateY(-4px)';
    }, { passive: true });
    document.addEventListener('pointerout', function (e) {
      var card = e.target.closest('.product-card');
      if (!card || card.contains(e.relatedTarget)) return;
      card.style.transform = '';
      card.classList.remove('tilting');
    }, { passive: true });
  }
})();
